import React, { useCallback, useMemo, useState } from "react";
import {
  ALL_GROUP_KEY,
  Box,
  Button,
  createColumnHelper,
  DataTable,
  DataTableCellContent,
  DataTableCellTrigger,
  DataTableEmpty,
  DataTableHoverActionsCell,
  DataTableToolbar,
  DataTableToolbarActions,
  Icon,
  Separator,
  TabsViewIcon,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  useIsMobile,
  type DataTableColumnDef,
  type DataTableGroupType,
  type Row,
  Skeleton,
} from "@salestrak/ui";

import { PageHeader } from "#app/components/page-header";
import { EntityTag } from "#app/components/entity";
import { ViewSearch } from "#app/components/filters/view-search";
import { AnalyticsContext, DashboardEvent, useTrackSearch } from "#app/lib/analytics";
import { copyText } from "#app/lib/utils/copy-actions";
import { DateText } from "#app/components/date-text";
import { useAppData } from "#app/lib/data";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { useTableViewSettings } from "#app/lib/hooks/use-table-view-settings";
import { useWorkspace } from "#app/lib/data/workspace-context";
import {
  useMemberDirectory,
  useMemberEntityResolver,
  useMemberLabelResolver,
} from "#app/lib/data/member-directory-provider";
import {
  dashboardSnapshot,
  surfaceHostName,
  surfaceOwnedBy,
  surfaceRefKey,
  useDashboardSurfaces,
  type DashboardSurface,
} from "./use-dashboard-surfaces";
import { DashboardActionsProvider, useDashboardActions } from "./dashboard-actions";
import { NewDashboardDialog } from "./new-dashboard-dialog";
import { useDashboardFavorites } from "./use-dashboard-favorites";
import { useRecentDashboards } from "./use-recent-dashboards";
import { dashboardVisibilityLabel } from "./workspace-dashboard-page";
import { workspaceHref } from "#app/lib/utils/entity-hrefs";

const h = createColumnHelper<DashboardSurface>();

// "Created" ships hidden — the reference set; the picker unhides it.
// Module constant: a fresh array per render churns the settings hook's memos.
const DEFAULT_HIDDEN_COLUMN_IDS = ["createdAt"];

// The identity column anchors every row: pinning takes it out of the
// reorderable span, so neither the header context menu nor a drag can move
// it — or land another column ahead of it (the workflows-table treatment).
const DASHBOARDS_PINNED_COLUMN_IDS = ["name"];

/** Which slice of the directory a dashboards list shows: every reachable
 *  surface, or only the ones the viewer authored. */
export type DashboardListScope = "all" | "mine";

const SCOPE_TITLE: Record<DashboardListScope, string> = {
  all: "All Dashboards",
  mine: "My Dashboards",
};

/**
 * The Dashboards module's directory view — one row per dashboard VIEW. The
 * row wears the dashboard's own identity (the renameable view name and its
 * icon/color, the same three the host's tab strip shows). The Location
 * column names what it reads from: a workflow dashboard's host, which routes
 * to the workflow itself, or a workspace dashboard's workflow set as chips.
 * `useWorkspaceData()`'s workflow set and the by-type view list are both
 * visibility-scoped by the API, so permissions come for free.
 *
 * "All Dashboards" and "My Dashboards" are the same table over two slices —
 * `scope="mine"` keeps only the views the viewer authored (a seeded built-in
 * has no author, so it belongs to nobody and never shows there). Both share
 * one `useTableViewSettings` key on purpose: they are one table to the viewer,
 * so a column arrangement follows them across the two routes.
 */
export function DashboardsScreen({ scope }: { scope: DashboardListScope }) {
  return (
    <DashboardActionsProvider>
      <DashboardsScreenBody scope={scope} />
    </DashboardActionsProvider>
  );
}

function DashboardsScreenBody({ scope }: { scope: DashboardListScope }) {
  const { push } = useAppNavigate();
  const { renderMenuItems } = useDashboardActions();
  const isMobile = useIsMobile();
  const { activeWorkspace } = useWorkspace();
  const { currentUser } = useAppData();
  const resolveMember = useMemberEntityResolver();
  const resolveMemberLabel = useMemberLabelResolver();
  const { pendingOf } = useMemberDirectory();
  const { surfaces, isLoaded } = useDashboardSurfaces();
  const { isFavorite, toggleFavorite } = useDashboardFavorites();
  const { recordVisit } = useRecentDashboards();
  const [createOpen, setCreateOpen] = useState(false);
  const tableView = useTableViewSettings("dashboards", {
    defaultHiddenColumnIds: DEFAULT_HIDDEN_COLUMN_IDS,
  });

  const title = SCOPE_TITLE[scope];
  const scopedSurfaces = useMemo(
    () =>
      scope === "mine"
        ? surfaces.filter((surface) =>
            surfaceOwnedBy(surface, currentUser?.userId),
          )
        : surfaces,
    [scope, surfaces, currentUser?.userId],
  );

  // The surfaces are already in hand (derived from workspace data), so the
  // toolbar search narrows them here rather than through a query: a match on
  // the dashboard's own name or on its host — the two names the row shows.
  const [searchValue, setSearchValue] = useState("");
  const visibleSurfaces = useMemo(() => {
    const needle = searchValue.trim().toLowerCase();
    if (!needle) return scopedSurfaces;
    return scopedSurfaces.filter(
      (surface) =>
        surface.name.toLowerCase().includes(needle) ||
        surfaceHostName(surface).toLowerCase().includes(needle),
    );
  }, [scopedSurfaces, searchValue]);
  useTrackSearch(searchValue, () => ({
    context: AnalyticsContext.Dashboard,
    event: DashboardEvent.Search,
    data: { resultCount: visibleSurfaces.length },
  }));
  // The toolbar yields only when there is nothing to search: a zero-result
  // search keeps it, since the field is how the viewer widens the view again.
  const isTrulyEmpty = isLoaded && scopedSurfaces.length === 0 && !searchValue;

  const workspacePath = activeWorkspace?.urlPath ?? "";
  const copyDashboardLink = useCallback(
    (surface: DashboardSurface) => {
      void copyText(workspaceHref(workspacePath, surface.route), "Link");
    },
    [workspacePath],
  );

  const openLocation = useCallback(
    (surface: DashboardSurface) => {
      if (surface.scope.kind !== "workflow") return;
      push(`/workflow/${surface.scope.id}`);
    },
    [push],
  );

  const columns = useMemo<DataTableColumnDef<DashboardSurface>[]>(
    () => [
      {
        id: "name",
        header: "Name",
        accessorFn: (surface) => surface.name,
        // The identity column anchors every row — the picker locks it on.
        enableHiding: false,
        // The workflows-table name rung: 250px floor, 320px packed base.
        meta: { minWidth: 250, truncate: true, width: 320 },
        cell: ({ row }) => {
          const surface = row.original;
          const favorited = isFavorite(surfaceRefKey(surface));
          const visibility = dashboardVisibilityLabel(surface);
          return (
            <DataTableHoverActionsCell
              actions={
                <>
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <Button
                          variant="outline"
                          size="icon-sm"
                          aria-label={
                            favorited
                              ? "Remove from favorites"
                              : "Add to favorites"
                          }
                          onClick={(e: React.MouseEvent) => {
                            // The row navigates on click; an action press must
                            // not also open the dashboard.
                            e.stopPropagation();
                            toggleFavorite(surface, "directory_star");
                          }}
                        >
                          {/* `warning` is the favorite star's gold everywhere
                              it is lit (global search's favorited rows). Lit =
                              FILLED: the `star-filled` glyph carries the gold, and the tight 1px
                              drop-shadow (a filter, so it hugs the star's
                              shape) keeps the fill legible on white/light
                              fills where gold alone washes out. */}
                          <Icon
                            name={favorited ? "star-filled" : "star"}
                            color={favorited ? "warning" : "muted"}
                            className={
                              favorited
                                ? "drop-shadow-[0_1px_1px_rgb(0_0_0/0.35)]"
                                : undefined
                            }
                          />
                        </Button>
                      }
                    />
                    <TooltipPopup side="top">
                      {favorited ? "Remove from favorites" : "Add to favorites"}
                    </TooltipPopup>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <Button
                          variant="outline"
                          size="icon-sm"
                          aria-label="Copy link"
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            copyDashboardLink(surface);
                          }}
                        >
                          <Icon name="link" color="muted" />
                        </Button>
                      }
                    />
                    <TooltipPopup side="top">Copy link</TooltipPopup>
                  </Tooltip>
                </>
              }
            >
              <Box direction="row" align="center" gap="sm" minWidth={0}>
                <TabsViewIcon
                  color={surface.color}
                  icon={surface.icon}
                  variant="compact-tile"
                />
                <Text size="sm" weight="medium" truncate>
                  {surface.name}
                </Text>
                {visibility.icon !== "globe" ? (
                  // Visibility rides the name as a bare glyph: a lock for a
                  // private dashboard, the people glyph for a shared one.
                  // Public is the unmarked default, so it wears nothing.
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <Icon
                          name={visibility.icon}
                          size="sm"
                          color="muted"
                          aria-label={visibility.label}
                        />
                      }
                    />
                    <TooltipPopup side="top">{visibility.label}</TooltipPopup>
                  </Tooltip>
                ) : null}
              </Box>
            </DataTableHoverActionsCell>
          );
        },
      },
      {
        id: "location",
        header: "Location",
        accessorFn: surfaceHostName,
        meta: { minWidth: 240, maxWidth: 360 },
        cell: ({ row }) => {
          const { scope } = row.original;
          if (scope.kind === "workspace") {
            // A workspace dashboard reads from a SET, so its cell states the
            // set and is no single destination: bare content, and the row
            // click stays the row's. The leading workflow wears the same tag
            // as a single-workflow row so the column reads as one list; the
            // rest fold into a muted "+N", and the tooltip names everyone.
            const [lead, ...rest] = scope.workflows;
            if (lead === undefined) {
              return (
                <DataTableCellContent>
                  <Text size={null} variant="muted">
                    —
                  </Text>
                </DataTableCellContent>
              );
            }
            const leadTag = (
              <EntityTag
                entity={{ kind: "workflow", id: lead.workflowId, workflow: lead }}
                interactive={false}
                meta={rest.length > 0 ? `+${rest.length}` : undefined}
              />
            );
            if (rest.length === 0) {
              return <DataTableCellContent>{leadTag}</DataTableCellContent>;
            }
            return (
              <DataTableCellContent>
                <Tooltip>
                  <TooltipTrigger
                    render={<Box direction="row" align="center" minWidth={0} />}
                  >
                    {leadTag}
                  </TooltipTrigger>
                  <TooltipPopup side="top">
                    <Box gap="2xs">
                      {scope.workflows.map((workflow) => (
                        <Text key={workflow.workflowId} size="xs">
                          {workflow.name}
                        </Text>
                      ))}
                    </Box>
                  </TooltipPopup>
                </Tooltip>
              </DataTableCellContent>
            );
          }
          // The host entity is its own destination: the cell routes to the
          // workflow itself, distinct from the row's dashboard navigation
          // (the teams-table Teammates-count grammar — outline hover, its own
          // press target, row click stays claimed by the button).
          return (
            <DataTableCellTrigger onClick={() => openLocation(row.original)}>
              <DataTableCellContent>
                <EntityTag
                  entity={{
                    kind: "workflow",
                    id: scope.id,
                    workflow: scope.workflow,
                  }}
                  interactive={false}
                />
              </DataTableCellContent>
            </DataTableCellTrigger>
          );
        },
      },
      {
        id: "viewed",
        header: "Last viewed",
        // Epoch ms accessor so the sort is chronological; unviewed rows sort
        // together at zero.
        accessorFn: (surface) =>
          surface.view.viewerLastViewedAt
            ? Date.parse(surface.view.viewerLastViewedAt)
            : 0,
        meta: { minWidth: 120, textBrightness: "subtle" },
        cell: ({ row }) => {
          return (
            <DataTableCellContent>
              <DateText date={row.original.view.viewerLastViewedAt ?? null} />
            </DataTableCellContent>
          );
        },
      },
      h.date({ accessorKey: "createdAt", header: "Created" }),
      h.date({ accessorKey: "updatedAt", header: "Updated" }),
      {
        id: "owner",
        header: "Owner",
        accessorFn: (surface) =>
          (surface.ownerId ? resolveMemberLabel(surface.ownerId) : null) ?? "",
        // An avatar-only value: the same floor the other avatar columns keep.
        meta: { minWidth: 100 },
        cell: ({ row }) => {
          const owner = row.original.ownerId
            ? resolveMember(row.original.ownerId)
            : null;
          if (!owner) {
            // Built-in dashboards were seeded by the system — no author row.
            return (
              <DataTableCellContent>
                {row.original.ownerId && pendingOf(row.original.ownerId) ? (
                  <Skeleton circle delayed width={20} />
                ) : (
                  <Text size={null} variant="muted">
                    —
                  </Text>
                )}
              </DataTableCellContent>
            );
          }
          return (
            // A cell never labels a member: the avatar alone, hovering to the
            // member preview that carries the name. The owner cannot be
            // changed here, so the cell wears no ring — the ring is a promise
            // the cell edits, and opening the profile is not an edit. Owner
            // cells carry the live presence dot: whether the owner is
            // reachable right now is part of what the column answers.
            <DataTableCellContent>
              <EntityTag
                entity={owner}
                display="visual"
                ghost={false}
                presence
              />
            </DataTableCellContent>
          );
        },
      },
    ],
    [
      isFavorite,
      toggleFavorite,
      copyDashboardLink,
      openLocation,
      resolveMember,
      resolveMemberLabel,
      pendingOf,
    ],
  );

  const groups = useMemo<DataTableGroupType<DashboardSurface>[]>(
    () => [
      {
        key: ALL_GROUP_KEY,
        label: title,
        data: visibleSurfaces,
        totalCount: visibleSurfaces.length,
      },
    ],
    [title, visibleSurfaces],
  );

  const handleOpen = useCallback(
    (row: Row<DashboardSurface>) => {
      const surface = row.original;
      recordVisit(dashboardSnapshot(surface));
      push(surface.route);
    },
    [recordVisit, push],
  );

  return (
    <Box direction="column" fill="flex">
      <PageHeader
        title={title}
        actions={
          // The create action opens the Create Dashboard dialog (name and
          // workflows, then the row exists and the page lands on its template
          // chooser).
          <Button
            aria-label="Create Dashboard"
            size={isMobile ? "icon-xs" : "xs"}
            onClick={() => setCreateOpen(true)}
          >
            {/* size={null}: the 24px action's label inherits the button rung's
                12px instead of resetting to Text's own default. */}
            {isMobile ? <Icon name="plus" /> : <Text size={null}>Create Dashboard</Text>}
          </Button>
        }
      />
      <Separator />
      {/* The view toolbar (the teams/workflows roster band): one search, the
        * view's `/` target. Filters and view settings join it as the module
        * grows its list views. */}
      {!isTrulyEmpty && (
        <DataTableToolbar>
          <DataTableToolbarActions>
            <ViewSearch
              onValueChange={setSearchValue}
              size="xs"
            />
          </DataTableToolbarActions>
        </DataTableToolbar>
      )}
      <Box direction="column" fill="flex" paddingBottom="lg">
        <DataTable
          columns={columns}
          groups={groups}
          getRowId={surfaceRefKey}
          loading={!isLoaded}
          stickyHeader
          gutterTop="md"
          density="regular"
          pinnedColumnIds={DASHBOARDS_PINNED_COLUMN_IDS}
          paddingX="lg"
          onRowClick={handleOpen}
          // The dashboard's roster on the row's right-click and its kebab —
          // the same actions the module menu's rows answer with.
          renderRowActions={renderMenuItems}
          {...tableView.tableProps}
          // Tag-manager grammar: opting into onColumnVisibilityChange puts
          // the circle-plus show/hide picker on the actions-column header.
          onColumnVisibilityChange={
            tableView.settingsProps.onColumnVisibilityChange
          }
          emptyState={
            <DataTableEmpty
              message={
                searchValue
                  ? "No dashboards match your search"
                  : scope === "mine"
                    ? "You haven't created any dashboards yet"
                    : "No dashboards yet"
              }
            />
          }
        />
      </Box>
      <NewDashboardDialog open={createOpen} onOpenChange={setCreateOpen} via="directory" />
    </Box>
  );
}
